// Canonical form used for tolerant company-name matching in the browser.
//
// NORMALIZATION PARITY — this mirrors Ruby `TextNormalizer.normalize_company_name`
// and the SQL built by `Search::NormalizedNameFilter.normalized_sql`. All three
// strip diacritics, the punctuation set below and every whitespace character.
// Do not change one without the other two (LUC-705).
//
// Diacritics are removed via NFD decomposition, which covers the Slovak, Czech
// and Romanian letters we care about. Non-decomposable letters (ł, đ) keep their
// stroke here — this is a browser-side nicety over the options already on the
// page, not a correctness guarantee; the server query behind the same input
// normalizes both sides with Postgres `unaccent`.
// Includes the smart quotes and dashes a copy-pasted company name carries.
const PUNCTUATION = /[.,;:'"\-_()&\u2018\u2019\u201c\u201d\u2011\u2013\u2014]/g
const COMBINING_MARKS = /[\u0300-\u036f]/g
// JS `\s` already covers the non-breaking and typographic spaces.
const WHITESPACE = /\s+/g

export const normalizeCompanyName = (value) => {
  if (value === null || value === undefined) return ""

  return String(value)
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(COMBINING_MARKS, "")
    .replace(PUNCTUATION, "")
    .replace(WHITESPACE, "")
}

// Matches the way the server searches: a plain case-insensitive substring first,
// then the punctuation/spacing-tolerant comparison ("R+S service" must find
// "R + S service"). Keeping the plain check means a search for "e c" still
// matches "Acme Corp", which the normalized form alone would drop.
export const matchesCompanyName = (text, search) => {
  const needle = String(search ?? "").trim()
  if (needle === "") return true

  const haystack = String(text ?? "")
  if (haystack.toLowerCase().includes(needle.toLowerCase())) return true

  const normalizedNeedle = normalizeCompanyName(needle)
  if (normalizedNeedle === "") return true

  return normalizeCompanyName(haystack).includes(normalizedNeedle)
}
